// The two things BOTH `store.js` and `stores/strategies.js` need, in a module
// that imports neither.
//
// WHY THIS FILE EXISTS. `store.js` has to resolve a pad id that names one of
// your saved strategies, and `stores/strategies.js` has to read and write the
// risk profile that holds the pad. Pointing them at each other is a circular
// import, and a circular import is only safe while nobody touches the other
// side during evaluation — which held right up until `storeReady`'s promise
// resolved one tick too early and wrote to a binding that was still undefined.
//
// A cycle that works is a cycle waiting for a module-order change. This leaf
// has no imports of its own, so there is nothing to be in the middle of.

import { ref } from 'vue';

/** The `records` entity a saved strategy is stored as. */
export const STRATEGY_ENTITY = 'strategy';

/**
 * The strategies someone has saved. Filled at boot from the one `records.all()`
 * that `store.js` already does, and kept current by `stores/strategies.js`.
 * Empty in /demo.
 */
export const myStrategies = ref([]);

// The keys the ENGINE understands. `validateStrategy` rejects every key it does
// not know, so the bookkeeping a stored row carries — entity, baseId,
// createdAt, updatedAt, and whatever `records.put` stamps on — has to come off
// before the document is validated or run.
const DOCUMENT_KEYS = [
  'id', 'name', 'version', 'builtin', 'description', 'filters', 'probabilitySource',
  'activeLeg', 'legRule', 'params', 'combiner', 'staking', 'bookmakerRule', 'slots',
];

/** Just the strategy document, with everything the engine does not know stripped. */
export function toDocument(strategy) {
  const out = {};
  for (const key of DOCUMENT_KEYS) if (strategy && key in strategy) out[key] = strategy[key];
  return out;
}

/** Saved strategies read in a stable order — by name, then by id. */
export const byName = (a, b) =>
  String(a.name).localeCompare(String(b.name)) || String(a.id).localeCompare(String(b.id));
